import {
    IonButton,
    IonCol,
    IonInput,
    IonItem,
    IonLabel,
    IonRow,
} from "@ionic/react";
import React, { useEffect, useState } from "react";
import ErrorAlert from "../Errors/ErrorAlert";
import SuccessBanner from "../Banners/SuccessBanner";
import { BarnPerson } from "../../interfaces/Person";
import { savePersonBasicInfo, savePersonRiderInfo } from "../../utilities/barn/BarnPerson"; 
import RiderSelectStatus from "../Rider/RiderSelectStatus";
import Spinner from "../Spinners/Spinner";
import RequiredInputIndicator from "../Forms/RequiredInputIndicator";
import { Rider } from "../../models";

interface _Props {
    barnPerson: BarnPerson
    onSubmit: Function
}

const EditPersonBasicInfoForm: React.FC<_Props> = ({barnPerson, onSubmit}) => {    
    const [name, setName] = useState("");

    const [rider, setRider] = useState<Rider | null>(null);
    const [birthdate, setBirthdate] = useState<string>("");
    const [isProfessional, setIsProfessional] = useState<boolean | null | undefined>(false);
    
    const [success, setSuccess] = useState("");
    const [error, setError] = useState("");

    const [isLoading, setIsLoading] = useState(false);

    const clearForm = () => {
        setName("");
        setBirthdate("");
        setIsProfessional(false);
    }

    useEffect(() => {
        const getData = (bp: BarnPerson) => {
            if (bp && bp.name) {
                setName(bp.name);
            } else {
                setName("");
            }
    
            if (bp && bp.fullRoles && bp.fullRoles.length > 0) {
                for (let i = 0; i < bp.fullRoles.length; i++) {
                    const fullRole = bp.fullRoles[i];
                    if (fullRole.role === "rider") {
                        if (fullRole.rider) setRider(fullRole.rider);
                        setBirthdate(fullRole.rider?.birthdate || "");
                        setIsProfessional(fullRole.rider?.isProfessional);
                    }
                }
            } else {
                setBirthdate("");
                setIsProfessional(false);
            }
        }
        
        if (barnPerson) getData(barnPerson)
        else clearForm();
    }, [barnPerson]);

    const handleIsProfessionalInput = (isProfessional: boolean | null | undefined) => {
        const professionalValue = isProfessional ?? false;
        setIsProfessional(professionalValue);
    }

    const verifyForm = () => {
        if (!name) {
            setError("Please include a name for the person");
            return false;
        }
        return true;
    }

    const handleEditPerson = async () => {
        setIsLoading(true);
        setError("");
        setSuccess("");
        const isValid = verifyForm();
        if (isValid) {
            setError("");
            const result = await savePersonBasicInfo(barnPerson, name);
            if (result.isSuccess) {
                if (rider) {
                    const riderResult = await savePersonRiderInfo(barnPerson, birthdate, !!isProfessional);
                    if (riderResult.isSuccess) {
                        setSuccess("Successfully updated the info!");
                    } else {
                        setError("Could not update the info.");
                    }
                } else {
                    if (result.isSuccess) {
                        setSuccess("Successfully updated the info!");
                    } else {
                        setError("Could not update the info.");
                        
                    }
                }
            }
            onSubmit();
        }
        setIsLoading(false);
    }

    return (
        <>
            {isLoading ?
                <Spinner />
                :
                <>
                    {success && <SuccessBanner success={success} />}
                    {error && <ErrorAlert width="12" error={error} />}
                    <IonRow>
                        <IonCol size="12">
                            <IonItem color="white">
                                <IonLabel position="stacked">
                                    Full Name
                                    <RequiredInputIndicator />
                                </IonLabel>
                                <IonInput 
                                    type="text"
                                    value={name}
                                    aria-required={true}
                                    onIonChange={e => {
                                        setName(e.detail.value!);
                                    }}
                                />
                            </IonItem>
                        </IonCol>
                    </IonRow>
                    {rider && (
                        <>
                            <IonRow>
                                <IonCol sizeXs="12" sizeMd="6">
                                    <IonItem color="white">
                                        <IonLabel position="stacked">Birth Date</IonLabel>
                                        <IonInput 
                                            type="date"
                                            value={birthdate}
                                            aria-required={false}
                                            onIonChange={e => {
                                                setBirthdate(e.detail.value!);
                                            }}
                                        />
                                    </IonItem>
                                </IonCol>
                                <IonCol sizeXs="12" sizeMd="6">
                                    <RiderSelectStatus rider={rider} onChange={handleIsProfessionalInput} />
                                </IonCol>
                            </IonRow>
                        </>
                    )}
                    <IonRow className="ion-justify-content-center">
                        <IonCol sizeMd="4" className="ion-text-center">
                            <IonButton
                                className="ion-margin-top"
                                color="tertiary"
                                expand="block"
                                onClick={handleEditPerson}
                            >
                                Save
                            </IonButton>
                        </IonCol>
                    </IonRow>
                </>
            }
        </>
    );
};

export default EditPersonBasicInfoForm;